import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { initialOrdemCompraConsultarPrecosFilters, useOrdemCompraConsultarPrecosFilters } from "../hooks/OrdemCompra_consultarPrecos/filters";
import { useOrdemCompraConsultarPrecosPagination } from "../hooks/OrdemCompra_consultarPrecos/pagination";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { OrdemCompraDropDown } from "../../../../common/domain/models/ordem_compra_dropdown";
import { ProdutoServicoDropDown } from "../../../../common/domain/models/produto_servico_dropdown";
import { processError } from "../../../../common/utils/base_error";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { OrdemCompraPreco } from "../../domain/models/ordem_compra_preco";
import { OrdemCompraTotalValor } from "../../domain/models/ordem_compra_total_valor";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { ReportComprasForm } from "../schemas/OrdemCompra_consultarPrecos/report_compras";

import dayjs from "dayjs";

/**
 * LoadOrdemComprasFuncState
 */

type LoadOrdemComprasFuncState = {
  centroCustos?: number[] | null;
  titulo?: string | null;
  fornecedores?: number[] | null;
  dataOrdemCompraDe?: Date | null;
  dataOrdemCompraAte?: Date | null;
  ordemCompras?: number[] | null;
  produtosServicos?: number[] | null;
  dataPagoDe?: Date | null;
  dataPagoAte?: Date | null;
  subcontas?: number[] | null;
  statusOC?: number[] | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * LoadOrdemCompraValorTotalFuncState
 */

type LoadOrdemCompraValorTotalFuncState = {
  centroCustos?: number[] | null;
  titulo?: string | null;
  fornecedores?: number[] | null;
  dataOrdemCompraDe?: Date | null;
  dataOrdemCompraAte?: Date | null;
  ordemCompras?: number[] | null;
  produtosServicos?: number[] | null;
  dataPagoDe?: Date | null;
  dataPagoAte?: Date | null;
  subcontas?: number[] | null;
  statusOC?: number[] | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useOrdemCompraConsultarPrecosViewModel
 */

export const useOrdemCompraConsultarPrecosViewModel = () => {
  const {
    compras: {
      getAllOrdemCompraPrecosUseCase,
      getOrdemCompraTotalValoresUseCase,
    },
    reports: {
      getReportComprasRelatorioComprasUseCase,
    }
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadFornecedoresDropDown,
    loadOrdemComprasDropDown,
    loadProdutoServicosDropDown,
    loadSubContasDropDown,
  } = useDropDownLoader();

  const debouncedHandler = useCreateDebounceHandler(750);

  const { pushErrorNotification } = usePageLoader();

  const { idUsuario } = useUserAuthStore();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();
  const [fornecedoresDropdown, setFornecedoresDropdown] = useState<FornecedorDropDown[]>();
  const [ordemComprasDropdown, setOrdemComprasDropdown] = useState<OrdemCompraDropDown[]>();
  const [produtoServicosDropdown, setProdutoServicosDropdown] = useState<ProdutoServicoDropDown[]>();
  const [subcontasDropdown, setSubcontasDropdown] = useState<DropDown[]>();

  const [ordemCompraTotalValores, setOrdemCompraTotalValores] = useState<OrdemCompraTotalValor>();
  const [ordemCompraPrecos, setOrdemCompraPrecos] = useState<OrdemCompraPreco[]>();

  const [comprasReportModal, setComprasReportModal] = useState(false);

  const filters = useOrdemCompraConsultarPrecosFilters();
  const pagination = useOrdemCompraConsultarPrecosPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  /**
   * Funcoes de carregamento
   */

  const loadOrdemCompras = useCallback(async (fs?: LoadOrdemComprasFuncState) => {
    try {
      const filteredCentroCustos = evaluateParamValue(fs?.centroCustos, filters.centroCustos);
      const filteredTitulo = evaluateParamValue(fs?.titulo, filters.titulo);
      const filteredFornecedores = evaluateParamValue(fs?.fornecedores, filters.fornecedores);
      const filteredDataOrdemCompraDe = evaluateParamValue(fs?.dataOrdemCompraDe, filters.dataOrdemCompraDe);
      const filteredDataOrdemCompraAte = evaluateParamValue(fs?.dataOrdemCompraAte, filters.dataOrdemCompraAte);
      const filteredOrdemCompras = evaluateParamValue(fs?.ordemCompras, filters.ordemCompras);
      const filteredProdutosServicos = evaluateParamValue(fs?.produtosServicos, filters.produtosServicos);
      const filteredDataPagoDe = evaluateParamValue(fs?.dataPagoDe, filters.dataPagoDe);
      const filteredDataPagoAte = evaluateParamValue(fs?.dataPagoAte, filters.dataPagoAte);
      const filteredSubcontas = evaluateParamValue(fs?.subcontas, filters.subcontas);
      const filteredStatusOC = evaluateParamValue(fs?.statusOC, filters.statusOC);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllOrdemCompraPrecosUseCase.getAllOrdemCompraPrecos(
        filteredCentroCustos,
        filteredTitulo,
        filteredFornecedores,
        filteredDataOrdemCompraDe,
        filteredDataOrdemCompraAte,
        filteredOrdemCompras,
        filteredProdutosServicos,
        filteredDataPagoDe,
        filteredDataPagoAte,
        filteredSubcontas,
        filteredStatusOC,
        filteredPage,
        filteredRowsPerPage,
      );

      setOrdemCompraPrecos(getAllOrdemCompraPrecosUseCase.items);
      pagination.setTotalItems(getAllOrdemCompraPrecosUseCase.totalItems);
    } catch (e: unknown) {
      setOrdemCompraPrecos(undefined);
      throw new Error('Falha ao buscar dados da pagina!');
    }
  }, [
    getAllOrdemCompraPrecosUseCase,
    filters,
    pagination,
  ]);

  const loadOrdemCompraTotalValores = useCallback(async (fs?: LoadOrdemCompraValorTotalFuncState) => {
    try {
      const filteredCentroCustos = evaluateParamValue(fs?.centroCustos, filters.centroCustos);
      const filteredTitulo = evaluateParamValue(fs?.titulo, filters.titulo);
      const filteredFornecedores = evaluateParamValue(fs?.fornecedores, filters.fornecedores);
      const filteredDataOrdemCompraDe = evaluateParamValue(fs?.dataOrdemCompraDe, filters.dataOrdemCompraDe);
      const filteredDataOrdemCompraAte = evaluateParamValue(fs?.dataOrdemCompraAte, filters.dataOrdemCompraAte);
      const filteredOrdemCompras = evaluateParamValue(fs?.ordemCompras, filters.ordemCompras);
      const filteredProdutosServicos = evaluateParamValue(fs?.produtosServicos, filters.produtosServicos);
      const filteredDataPagoDe = evaluateParamValue(fs?.dataPagoDe, filters.dataPagoDe);
      const filteredDataPagoAte = evaluateParamValue(fs?.dataPagoAte, filters.dataPagoAte);
      const filteredSubcontas = evaluateParamValue(fs?.subcontas, filters.subcontas);
      const filteredStatusOC = evaluateParamValue(fs?.statusOC, filters.statusOC);

      const results = await getOrdemCompraTotalValoresUseCase.getOrdemCompraTotalValores(
        filteredCentroCustos,
        filteredTitulo,
        filteredFornecedores,
        filteredDataOrdemCompraDe,
        filteredDataOrdemCompraAte,
        filteredOrdemCompras,
        filteredProdutosServicos,
        filteredDataPagoDe,
        filteredDataPagoAte,
        filteredSubcontas,
        filteredStatusOC,
      );

      setOrdemCompraTotalValores(results);
    } catch (e: unknown) {
      setOrdemCompraTotalValores(undefined);
      throw new Error('Falha ao buscar dados da pagina!');
    }
  }, [
    filters,
    getOrdemCompraTotalValoresUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

      await loadOrdemComprasDropDown(dt => setOrdemComprasDropdown(dt.map(x => ({ idOrdemCompra: x.id, descricao: x.title }))));

      await loadProdutoServicosDropDown(dt => setProdutoServicosDropdown(dt), {});

      await loadSubContasDropDown(dt => setSubcontasDropdown(dt));

      await loadOrdemCompras();

      await loadOrdemCompraTotalValores();

    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

      await loadOrdemComprasDropDown(dt => setOrdemComprasDropdown(dt.map(x => ({ idOrdemCompra: x.id, descricao: x.title }))));

      await loadProdutoServicosDropDown(dt => setProdutoServicosDropdown(dt), {});

      await loadSubContasDropDown(dt => setSubcontasDropdown(dt));

      await loadOrdemCompras();

      await loadOrdemCompraTotalValores();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadFornecedoresDropDown,
    loadOrdemComprasDropDown,
    loadProdutoServicosDropDown,
    loadSubContasDropDown,
    loadOrdemCompras,
    loadOrdemCompraTotalValores,
    pushErrorNotification,
  ]);

  const handleComprasReportModalOpenState = useCallback((openState: boolean) => {
    setComprasReportModal(openState);
  }, [
  ]);

  const handleComprasReportModalSubmit = useCallback(async (form: ReportComprasForm) => {
    try {
      setLoading(true);
      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportComprasRelatorioComprasUseCase.getReportComprasRelatorioCompras(
            requiredMimeType,
            form.centroCustos,
            form.titulo,
            form.ordemCompras,
            form.subcontas,
            form.fornecedores,
            form.dataOC,
            form.produtoServicos,
            form.dataPago,
            form.statusOC,
            idUsuario,
          ),
      });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao gerar relatório!'));
    } finally {
      setLoading(false);
    }
  }, [
    idUsuario,
    getReportComprasRelatorioComprasUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useOrdemCompraConsultarPrecosPagination.setState({ page: page + 1 });
      await loadOrdemCompras({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useOrdemCompraConsultarPrecosPagination.setState({ page: 1, rowsPerPage });
      await loadOrdemCompras({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleCentroCustosChange = useCallback(async (centroCustos: number[] | null) => {
    try {
      setLoading(true);
      useOrdemCompraConsultarPrecosFilters.setState({ centroCustos });
      await loadOrdemCompras({ centroCustos });
      await loadOrdemCompraTotalValores({ centroCustos });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadOrdemCompras,
    loadOrdemCompraTotalValores,
  ]);

  const handleTituloChange = useDebouncedCallback(debouncedHandler, async (titulo: string | null) => {
    useOrdemCompraConsultarPrecosFilters.setState({ titulo });
    return async () => {
      try {
        setLoading(true);
        useOrdemCompraConsultarPrecosFilters.setState({ titulo });
        await loadOrdemCompras({ titulo });
        await loadOrdemCompraTotalValores({ titulo });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pushErrorNotification,
    loadOrdemCompras,
    loadOrdemCompraTotalValores,
  ]);

  const handleFornecedoresChange = useCallback(async (fornecedores: number[] | null) => {
    try {
      setLoading(true);
      useOrdemCompraConsultarPrecosFilters.setState({ fornecedores });
      await loadOrdemCompras({ fornecedores });
      await loadOrdemCompraTotalValores({ fornecedores });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadOrdemCompras,
    loadOrdemCompraTotalValores,
  ]);

  const handleDataOrdemCompraChange = useDebouncedCallback(debouncedHandler, async (de: Date | null, ate: Date | null) => {
    useOrdemCompraConsultarPrecosFilters.setState({ dataOrdemCompraDe: de, dataOrdemCompraAte: ate });
    return async () => {
      try {
        setLoading(true);
        await loadOrdemCompras({ dataOrdemCompraDe: de, dataOrdemCompraAte: ate });
        await loadOrdemCompraTotalValores({ dataOrdemCompraDe: de, dataOrdemCompraAte: ate });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadOrdemCompras,
    loadOrdemCompraTotalValores,
  ]);

  const handleOrdemComprasChange = useCallback(async (ordemCompras: number[] | null) => {
    try {
      setLoading(true);
      useOrdemCompraConsultarPrecosFilters.setState({ ordemCompras });
      await loadOrdemCompras({ ordemCompras });
      await loadOrdemCompraTotalValores({ ordemCompras });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadOrdemCompras,
    loadOrdemCompraTotalValores,
  ]);

  const handleProdutosServicosChange = useCallback(async (produtosServicos: number[] | null) => {
    try {
      setLoading(true);
      useOrdemCompraConsultarPrecosFilters.setState({ produtosServicos });
      await loadOrdemCompras({ produtosServicos });
      await loadOrdemCompraTotalValores({ produtosServicos });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadOrdemCompras,
    loadOrdemCompraTotalValores,
  ]);

  const handleDataPagoChange = useCallback(async (de: Date | null, ate: Date | null) => {
    try {
      setLoading(true);
      useOrdemCompraConsultarPrecosFilters.setState({ dataPagoDe: de, dataPagoAte: ate });
      const dataPagoDe = dayjs(de).isValid() ? de : null;
      const dataPagoAte = dayjs(ate).isValid() ? ate : null;
      await loadOrdemCompras({ dataPagoDe, dataPagoAte });
      await loadOrdemCompraTotalValores({ dataPagoDe, dataPagoAte });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadOrdemCompras,
    loadOrdemCompraTotalValores,
  ]);

  const handleSubcontasChange = useCallback(async (subcontas: number[] | null) => {
    try {
      setLoading(true);
      useOrdemCompraConsultarPrecosFilters.setState({ subcontas });
      await loadOrdemCompras({ subcontas });
      await loadOrdemCompraTotalValores({ subcontas });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadOrdemCompras,
    loadOrdemCompraTotalValores,
  ]);

  const handleStatusOCChange = useCallback(async (statusOC: number[] | null) => {
    try {
      setLoading(true);
      useOrdemCompraConsultarPrecosFilters.setState({ statusOC });
      await loadOrdemCompras({ statusOC });
      await loadOrdemCompraTotalValores({ statusOC });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadOrdemCompras,
    loadOrdemCompraTotalValores,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);

      useOrdemCompraConsultarPrecosFilters.setState({
        ...initialOrdemCompraConsultarPrecosFilters,
      });

      await loadOrdemCompras({ ...initialOrdemCompraConsultarPrecosFilters });
      await loadOrdemCompraTotalValores({ ...initialOrdemCompraConsultarPrecosFilters })

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
    loadOrdemCompraTotalValores,
  ]);

  return {
    loading,
    errorMessage,
    centroCustoDropdown: centroCustoDropdown ?? [],
    fornecedoresDropdown: fornecedoresDropdown ?? [],
    ordemComprasDropdown: ordemComprasDropdown ?? [],
    produtoServicosDropdown: produtoServicosDropdown ?? [],
    subcontasDropdown: subcontasDropdown ?? [],
    ordemCompraTotalValores: ordemCompraTotalValores ?? null,
    ordemCompraPrecos: ordemCompraPrecos ?? [],
    comprasReportModal,
    filters,
    pagination,
    reportViewerState,
    handleReload,
    handleComprasReportModalOpenState,
    handleComprasReportModalSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleCentroCustosChange,
    handleTituloChange,
    handleFornecedoresChange,
    handleDataOrdemCompraChange,
    handleOrdemComprasChange,
    handleProdutosServicosChange,
    handleDataPagoChange,
    handleSubcontasChange,
    handleStatusOCChange,
    handleClearFilters,
  };
}